import { MatchedOnBadges } from '@/components/MatchedOnBadges/MatchedOnBadges'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import type { SearchOrganizationsError } from '@/generated/http-clients/v3ApiComponents'
import type {
  OrgSearchResponse,
  OrgSearchResult,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  deriveOrgSearchState,
  displayValue,
  formatOrgCreatedAt,
} from '@/helpers/orgSearch'
import { Alert, Button, Skeleton, Table, Text, VStack } from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

type Props = {
  searchTerm: string
  search: UseQueryResult<OrgSearchResponse, SearchOrganizationsError>
}

export function OrganizationSearchResults({
  searchTerm,
  search,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const state = deriveOrgSearchState(searchTerm, search)

  switch (state.kind) {
    case 'idle':
      return null
    case 'tooShort':
      return (
        <SectionCard>
          <Text color="fg.muted">
            {t('accountManagement.orgSearch.tooShort')}
          </Text>
        </SectionCard>
      )
    case 'loading':
      return (
        <SectionCard data-testid="org-search-loading">
          <VStack align="stretch" gap={3}>
            <Skeleton h={6} />
            <Skeleton h={6} />
            <Skeleton h={6} />
          </VStack>
        </SectionCard>
      )
    case 'results':
      return (
        <VStack align="stretch" gap={4} width="100%">
          {state.truncated ? (
            <Alert.Root status="info">
              <Alert.Indicator />
              <Alert.Title>
                {t('accountManagement.orgSearch.truncated')}
              </Alert.Title>
            </Alert.Root>
          ) : null}
          <ResultsTable results={state.results} />
        </VStack>
      )
    case 'empty':
      return (
        <SectionCard>
          <Text>
            {t('accountManagement.orgSearch.empty', { query: searchTerm })}
          </Text>
        </SectionCard>
      )
    case 'invalid':
      return (
        <Alert.Root status="warning" role="alert">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {t('accountManagement.orgSearch.invalid.title')}
            </Alert.Title>
            <Alert.Description>
              {state.message ??
                t('accountManagement.orgSearch.invalid.fallback')}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )
    case 'disabled':
      return (
        <Alert.Root status="warning" role="alert">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {t('accountManagement.orgSearch.disabled.title')}
            </Alert.Title>
            <Alert.Description>
              {t('accountManagement.orgSearch.disabled.description')}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )
    case 'error':
      return (
        <Alert.Root status="error" role="alert">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {t('accountManagement.orgSearch.error.title')}
            </Alert.Title>
            <Alert.Description>
              {state.message ?? t('accountManagement.orgSearch.error.fallback')}
            </Alert.Description>
          </Alert.Content>
          <Button
            size="sm"
            variant="outline"
            loading={search.isFetching}
            onClick={() => void search.refetch()}
          >
            {t('accountManagement.orgSearch.error.retry')}
          </Button>
        </Alert.Root>
      )
  }
}

function ResultsTable({ results }: Readonly<{ results: OrgSearchResult[] }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()

  return (
    <SectionCard>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.id')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.name')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.ownerEmail')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.billingEntity')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.salesforceAccountId')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.createdAt')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('accountManagement.orgSearch.columns.matchedOn')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {results.map((result) => (
            <Table.Row
              key={result.id}
              cursor="pointer"
              _hover={{ bg: 'bg.muted' }}
              onClick={() =>
                navigate({
                  to: hivesOverviewOrganizationDetailsRoute.to,
                  params: { orgId: result.id },
                })
              }
            >
              <Table.Cell fontFamily="mono">
                <Link
                  to={hivesOverviewOrganizationDetailsRoute.to}
                  params={{ orgId: result.id }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {result.id}
                </Link>
              </Table.Cell>
              <TruncatedCell value={result.name} />
              <TruncatedCell value={result.ownerEmail} />
              <TruncatedCell value={result.billingEntity} />
              <TruncatedCell value={result.salesforceAccountId} />
              <Table.Cell>{formatOrgCreatedAt(result.createdAt)}</Table.Cell>
              <Table.Cell>
                <MatchedOnBadges
                  matchedOn={result.matchedOn}
                  matchedEmails={result.matchedEmails}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </SectionCard>
  )
}

function TruncatedCell({ value }: Readonly<{ value: string | undefined }>) {
  const shown = displayValue(value)
  return (
    <Table.Cell>
      <Text truncate maxW="240px" title={shown}>
        {shown}
      </Text>
    </Table.Cell>
  )
}
